import React from 'react';
import { EasyblocksEditor } from '@swell/easyblocks-editor';
import {
  getEasyblocksPagePropsWithConfigs,
  getEasyblocksComponentDefinitions,
} from '@swell/apps-sdk';
import pt from 'prop-types';
import cx from 'classnames';
import widgets, { WidgetContext } from 'components/storefront/editor/widgets';
import onPageDataChange from 'components/storefront/editor/data';
import SwellPicker from 'components/storefront/editor/pickers/picker';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';

import { getCanvasUrl } from 'components/storefront/editor/utils/editor';
import {
  SWELL_GLOBAL,
  setPageSettingsFields,
} from 'components/storefront/editor/utils/easyblocks';

import EasyblocksOverrides from './overrides';
import StorefrontEditorEasyblocksSkeleton from './skeleton';
import './easyblocks.scss';

const PICKERS = Object.freeze({
  large: SwellPicker,
  'large-3': SwellPicker,
  compact: SwellPicker,
});

export default class Easyblocks extends React.PureComponent {
  static propTypes = {
    client: pt.object,
    storefront: pt.object,
    page: pt.object.isRequired,
    editorConfig: pt.object,
    themeConfigs: pt.object,
    storefrontUrl: pt.string,
    viewport: pt.string,
    subheader: pt.bool,
    loading: pt.bool,
    onError: pt.func,
    onLoadEasyblocksConfig: pt.func,
  };

  static contextType = EasyblocksBackendContext;

  constructor(props) {
    super(props);

    const { client, storefront } = props;

    this.state = {
      easyblocksConfig: null,
      components: null,
      pageData: {},
      widgets: null,
      onPageDataChange: null,
      canvasURL: getCanvasUrl(client, storefront),
      documentId: null,
      readOnly: false,
    };
  }

  componentDidMount() {
    this.loadEasyblocksData();
  }

  componentDidUpdate(prevProps) {
    const { editorConfig } = this.props;

    if (editorConfig !== prevProps.editorConfig) {
      this.loadEasyblocksData();
    }
  }

  loadEasyblocksData() {
    const {
      page: { id: pageId },
      editorConfig,
      themeConfigs,
      onError,
      onLoadEasyblocksConfig,
    } = this.props;

    const { globals, allSections, pageSections, layoutSectionGroups } =
      editorConfig;

    try {
      const { easyblocksConfig } = getEasyblocksPagePropsWithConfigs(
        globals,
        allSections,
        pageSections,
        layoutSectionGroups,
        pageId,
      );

      setPageSettingsFields(easyblocksConfig, globals.page);

      const components = this.getEasyblocksComponents(
        allSections,
        layoutSectionGroups,
      );

      const template = {
        ...easyblocksConfig.templates[0],
        version:
          themeConfigs.results.find(
            (config) => config.file_path === `theme/templates/${pageId}.json`,
          )?.version || 1,
      };

      easyblocksConfig.backend = this.context.getEasyblocksBackend(pageId, {
        template,
      });

      console.log('loadEasyblocksData', {
        easyblocksConfig,
        components,
        globals,
        allSections,
      });

      onLoadEasyblocksConfig(easyblocksConfig);

      this.setState({
        easyblocksConfig,
        documentId: pageId,
        components,
        onPageDataChange: onPageDataChange(this.setPageData, this.props),
      });
    } catch (err) {
      console.error('Easyblocks', err);
      onError('Unable to load theme editor configuration.');
    }
  }

  getEasyblocksComponents(allSections, layoutSectionGroups) {
    return getEasyblocksComponentDefinitions(
      allSections,
      layoutSectionGroups,
      () => {
        // Use placeholders for all types
        return function PlaceholderComponent() {
          return <div />;
        };
      },
    );
  }

  setPageData = (data) => {
    this.setState({ pageData: { ...this.state.pageData, ...data } });
  };

  render() {
    const { loading, viewport, subheader } = this.props;
    const {
      easyblocksConfig,
      components,
      pageData,
      onPageDataChange,
      canvasURL,
      readOnly,
      documentId,
    } = this.state;

    if (!easyblocksConfig) {
      return null;
    }

    return (
      <div
        className={cx('storefront-editor--easyblocks', `viewport-${viewport}`, {
          loading,
          'with-sub-header': subheader,
        })}
      >
        {loading && <StorefrontEditorEasyblocksSkeleton />}
        <WidgetContext.Provider value={this.props}>
          <EasyblocksEditor
            config={easyblocksConfig}
            components={components}
            widgets={widgets}
            externalData={pageData}
            onExternalDataChange={onPageDataChange}
            canvasURL={canvasURL}
            rootTemplate={SWELL_GLOBAL}
            rootComponent={null}
            readOnly={readOnly}
            documentId={documentId}
            pickers={PICKERS}
            scale={false}
            disableShortcuts
          />
        </WidgetContext.Provider>
        <EasyblocksOverrides {...this.props} />
      </div>
    );
  }
}
